import { useState } from 'react';

interface Appointment {
  id: string;
  customer_name: string;
  customer_phone: string;
  start_time: string;
  end_time: string;
  notes?: string;
  status: string;
}

const APPOINTMENTS_API = 'https://readdy.ai/api/public/calendar/appointments/42dddeaa-cf73-4b0a-970a-40b0949d5822.3d7f20fc4445601fab9af025d13f4e0956a0dbb5e9f83a65479bf44990efb243';

function formatTime(datetime: string) {
  const timePart = datetime.split(' ')[1];
  if (!timePart) return '';
  const [hourStr, minuteStr] = timePart.split(':');
  let hour = parseInt(hourStr, 10);
  const minute = minuteStr || '00';
  const ampm = hour >= 12 ? 'PM' : 'AM';
  hour = hour % 12;
  if (hour === 0) hour = 12;
  return `${hour}:${minute} ${ampm}`;
}

function formatDate(datetime: string) {
  const datePart = datetime.split(' ')[0];
  if (!datePart) return '';
  const [year, month, day] = datePart.split('-').map(Number);
  const months = ['January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'];
  return `${months[month - 1]} ${day}, ${year}`;
}

export default function MyBookings() {
  const [phone, setPhone] = useState('');
  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const [cancellingId, setCancellingId] = useState<string | null>(null);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [confirmCancelId, setConfirmCancelId] = useState<string | null>(null);

  const handleSearch = async () => {
    if (!phone.trim()) return;
    setLoading(true);
    setMessage(null);
    setAppointments([]);
    try {
      const res = await fetch(`${APPOINTMENTS_API}?customer_phone=${encodeURIComponent(phone.trim())}`);
      const data = await res.json();
      const list: Appointment[] = Array.isArray(data) ? data : [];
      const upcoming = list.filter((a) => {
        const apptDate = a.start_time.split(' ')[0];
        const today = new Date().toISOString().split('T')[0];
        return apptDate >= today && a.status !== 'cancelled';
      });
      setAppointments(upcoming);
      setSearched(true);
    } catch {
      setMessage({ type: 'error', text: 'Unable to retrieve bookings. Please try again.' });
      setSearched(true);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = async (id: string) => {
    setCancellingId(id);
    setMessage(null);
    try {
      const res = await fetch(`${APPOINTMENTS_API}/${id}`, {
        method: 'DELETE',
      });
      if (res.ok) {
        setAppointments((prev) => prev.filter((a) => a.id !== id));
        setMessage({ type: 'success', text: 'Your appointment has been successfully cancelled.' });
      } else {
        setMessage({ type: 'error', text: 'Unable to cancel appointment. Please contact us directly.' });
      }
    } catch {
      setMessage({ type: 'error', text: 'Unable to cancel appointment. Please contact us directly.' });
    } finally {
      setCancellingId(null);
      setConfirmCancelId(null);
    }
  };

  return (
    <div className="bg-gradient-to-br from-slate-900 via-black to-slate-900 py-20">
      <div className="max-w-3xl mx-auto px-6">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 bg-yellow-500/20 border border-yellow-500/40 rounded-full px-4 py-2 mb-6">
            <i className="ri-calendar-todo-line text-yellow-400 text-sm"></i>
            <span className="text-yellow-300 text-sm font-medium whitespace-nowrap">Manage Appointments</span>
          </div>
          <h2 className="text-4xl font-bold text-white mb-4">My Bookings</h2>
          <p className="text-white/60 text-base leading-relaxed max-w-xl mx-auto">
            Enter the phone number you used when booking to view or cancel your upcoming appointments.
          </p>
        </div>

        {/* Search Box */}
        <div className="bg-slate-800/60 rounded-2xl p-8 border border-yellow-500/20 mb-8">
          <label className="block text-sm font-semibold text-yellow-400 mb-3 uppercase tracking-wide">
            Phone Number
          </label>
          <div className="flex gap-3">
            <div className="relative flex-1">
              <div className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center">
                <i className="ri-phone-line text-slate-400 text-lg"></i>
              </div>
              <input
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                placeholder="Enter your phone number"
                className="w-full pl-11 pr-4 py-3 bg-slate-900/60 border border-yellow-500/20 rounded-xl text-white placeholder-slate-500 focus:outline-none focus:border-yellow-500/50 text-sm"
              />
            </div>
            <button
              onClick={handleSearch}
              disabled={loading || !phone.trim()}
              className="px-6 py-3 bg-yellow-500 hover:bg-yellow-400 text-black font-bold rounded-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap flex items-center gap-2"
            >
              {loading ? (
                <div className="w-5 h-5 border-2 border-black border-t-transparent rounded-full animate-spin"></div>
              ) : (
                <i className="ri-search-line text-lg"></i>
              )}
              {loading ? 'Searching...' : 'Find Bookings'}
            </button>
          </div>
        </div>

        {/* Message */}
        {message && (
          <div className={`mb-6 p-4 rounded-xl flex items-start gap-3 ${
            message.type === 'success'
              ? 'bg-green-500/15 border border-green-500/30 text-green-300'
              : 'bg-red-500/15 border border-red-500/30 text-red-300'
          }`}>
            <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
              <i className={message.type === 'success' ? 'ri-checkbox-circle-line text-lg' : 'ri-error-warning-line text-lg'}></i>
            </div>
            <span className="text-sm">{message.text}</span>
          </div>
        )}

        {/* Results */}
        {searched && !loading && (
          <>
            {appointments.length === 0 ? (
              <div className="text-center py-14 bg-slate-800/40 rounded-2xl border border-yellow-500/10">
                <div className="w-16 h-16 flex items-center justify-center bg-slate-700/60 rounded-full mx-auto mb-4">
                  <i className="ri-calendar-close-line text-slate-400 text-3xl"></i>
                </div>
                <p className="text-white/60 text-base">No upcoming appointments found for this phone number.</p>
                <p className="text-slate-500 text-sm mt-2">Double-check the number or contact us for help.</p>
              </div>
            ) : (
              <div className="space-y-4">
                <p className="text-white/50 text-sm mb-2">
                  Found <span className="text-yellow-400 font-semibold">{appointments.length}</span> upcoming appointment{appointments.length !== 1 ? 's' : ''}
                </p>
                {appointments.map((appt) => (
                  <div
                    key={appt.id}
                    className="bg-slate-800/60 rounded-2xl border border-yellow-500/20 overflow-hidden"
                  >
                    <div className="p-6">
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex items-start gap-4">
                          <div className="w-12 h-12 flex items-center justify-center bg-yellow-500/20 rounded-xl flex-shrink-0">
                            <i className="ri-calendar-check-line text-yellow-400 text-2xl"></i>
                          </div>
                          <div>
                            <p className="text-white font-bold text-lg leading-tight">{appt.customer_name}</p>
                            <p className="text-yellow-400 font-semibold text-base mt-1">
                              {formatDate(appt.start_time)}
                            </p>
                            <p className="text-white/60 text-sm mt-0.5">
                              {formatTime(appt.start_time)} &ndash; {formatTime(appt.end_time)}
                            </p>
                            {appt.notes && (
                              <p className="text-slate-400 text-sm mt-2 italic">"{appt.notes}"</p>
                            )}
                          </div>
                        </div>
                        <div className="flex-shrink-0">
                          <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-green-500/15 border border-green-500/30 text-green-400 text-xs font-semibold rounded-full">
                            <span className="w-1.5 h-1.5 bg-green-400 rounded-full"></span>
                            Confirmed
                          </span>
                        </div>
                      </div>

                      {/* Cancel */}
                      <div className="mt-5 pt-4 border-t border-yellow-500/10">
                        {confirmCancelId === appt.id ? (
                          <div className="flex items-center gap-3">
                            <p className="text-white/70 text-sm flex-1">Are you sure you want to cancel this appointment?</p>
                            <button
                              onClick={() => handleCancel(appt.id)}
                              disabled={cancellingId === appt.id}
                              className="px-4 py-2 bg-red-500/80 hover:bg-red-500 text-white text-sm font-bold rounded-lg transition-all cursor-pointer whitespace-nowrap disabled:opacity-50 flex items-center gap-1.5"
                            >
                              {cancellingId === appt.id ? (
                                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                              ) : (
                                <i className="ri-close-circle-line"></i>
                              )}
                              Yes, Cancel
                            </button>
                            <button
                              onClick={() => setConfirmCancelId(null)}
                              className="px-4 py-2 bg-slate-700 hover:bg-slate-600 text-white text-sm font-medium rounded-lg transition-all cursor-pointer whitespace-nowrap"
                            >
                              Keep It
                            </button>
                          </div>
                        ) : (
                          <button
                            onClick={() => setConfirmCancelId(appt.id)}
                            className="flex items-center gap-2 text-sm text-red-400 hover:text-red-300 font-medium transition-colors cursor-pointer"
                          >
                            <div className="w-4 h-4 flex items-center justify-center">
                              <i className="ri-close-circle-line text-base"></i>
                            </div>
                            Cancel Appointment
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
